import React from 'react'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'

export const EDIT_BOOKING_TYPE_SECTIONS = [
  {
    id: 'edit-section-details',
    label: 'Details',
    pageTitle: 'Booking Type Details',
    fields: [
      'category',
      'title',
      'description',
      'location',
      'durationInMinutes',
      'startTimeIncrementInMinutes',
    ],
  },
  {
    id: 'edit-section-payment',
    label: 'Payment',
    pageTitle: 'How much does this booking type cost?',
    fields: ['price', 'paymentAccountId', 'lessonPacks'],
  },
  {
    id: 'edit-section-options',
    label: 'Booking Options',
    pageTitle: 'How should students book this offering?',
    fields: ['accessCode', 'confirmationMessage'],
  },
]

export const getActiveSectionIndex = sectionId =>
  Math.max(
    0,
    EDIT_BOOKING_TYPE_SECTIONS.findIndex(section => section.id === sectionId),
  )

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
  },
  stepItem: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
  },
  stepTrack: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: 18,
    marginRight: theme.spacing(2),
    flexShrink: 0,
    paddingTop: 3,
  },
  circle: {
    width: 16,
    height: 16,
    borderRadius: '50%',
    border: '2.5px solid #587C8C',
    backgroundColor: theme.palette.common.white,
    boxSizing: 'border-box',
    flexShrink: 0,
  },
  circleCompleted: {
    borderColor: '#0053FF',
    backgroundColor: '#0053FF',
  },
  circleActive: {
    borderColor: '#0053FF',
    backgroundColor: '#E8EFFF',
  },
  connector: {
    width: 2,
    height: 36,
    flexShrink: 0,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    backgroundImage:
      'repeating-linear-gradient(to bottom, #587C8C 0, #587C8C 3px, transparent 3px, transparent 6px)',
    backgroundRepeat: 'repeat-y',
    backgroundPosition: 'center top',
  },
  connectorCompleted: {
    backgroundImage:
      'repeating-linear-gradient(to bottom, #0053FF 0, #0053FF 3px, transparent 3px, transparent 6px)',
  },
  stepButton: {
    display: 'block',
    flex: 1,
    minWidth: 0,
    padding: 0,
    paddingBottom: theme.spacing(3.5),
    border: 'none',
    backgroundColor: 'transparent',
    textAlign: 'left',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  stepButtonLast: {
    paddingBottom: 0,
  },
  stepLabel: {
    display: 'block',
    fontSize: '1rem',
    fontWeight: 400,
    color: '#172024',
    lineHeight: 1.25,
    marginBottom: theme.spacing(0.25),
  },
  stepLabelActive: {
    color: '#0053FF',
  },
  stepNumber: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 400,
    color: '#757575',
    lineHeight: 1.25,
  },
}))

const EditBookingTypeSectionNav = ({ activeSection, onSectionClick }) => {
  const classes = useStyles()
  const activeIndex = getActiveSectionIndex(activeSection)

  return (
    <Box
      className={classes.root}
      component="nav"
      aria-label="Booking type sections"
    >
      {EDIT_BOOKING_TYPE_SECTIONS.map((section, index) => {
        const isActive = index === activeIndex
        const isCompleted = index < activeIndex
        const isLast = index === EDIT_BOOKING_TYPE_SECTIONS.length - 1
        const stepNumber = index + 1
        const connectorCompleted = index < activeIndex

        return (
          <Box key={section.id} className={classes.stepItem}>
            <Box className={classes.stepTrack} aria-hidden="true">
              <Box
                className={clsx(
                  classes.circle,
                  isCompleted && classes.circleCompleted,
                  isActive && classes.circleActive,
                )}
              />
              {!isLast ? (
                <Box
                  className={clsx(
                    classes.connector,
                    connectorCompleted && classes.connectorCompleted,
                  )}
                />
              ) : null}
            </Box>
            <button
              type="button"
              className={clsx(
                classes.stepButton,
                isLast && classes.stepButtonLast,
              )}
              onClick={() => onSectionClick(section.id)}
              aria-current={isActive ? 'step' : undefined}
            >
              <Typography
                component="span"
                className={clsx(
                  classes.stepLabel,
                  isActive && classes.stepLabelActive,
                )}
              >
                {section.label}
              </Typography>
              <Typography component="span" className={classes.stepNumber}>
                Step {stepNumber}
              </Typography>
            </button>
          </Box>
        )
      })}
    </Box>
  )
}

export default EditBookingTypeSectionNav
